import { useMemo, useState } from "react";
import { SearchOrder } from "../../shared/models/search-options.model";
import { useCreateFilterStateBranch, useCustomPropertiesFilter, useDisplayPropertiesFilter, useSearchOptionsFilter, useSystemPropertiesFilter } from "../../state/FilterContext";
import { FilterState } from "../../state/state-models";
import { PropertyValue } from "../../shared/models/metadata.model";
import { TextExtractor, findFirstComponentOfType } from "../internal/TextExtractor";
import React from "react";
import { urlQueryKeys } from "../../state/state-url-management";

interface BranchStateProps {
    children: React.ReactNode,
    unit: "baseline" | "configuration" | "custom" | "display" | "organization" | "searchorder" | "system" | "text" | "validity";
    change: "clear" | "set";
    name?: string,
    value: string,
    direction: string,
}

function normalizePropertyValue(value: PropertyValue): PropertyValue {
    return typeof value === "string" ? value.trim() : value;
}

function mergePropertyValues(existingValues: PropertyValue[] | undefined, incomingValue: string): PropertyValue[] {
    const mergedValues = (existingValues || []).map(normalizePropertyValue);
    const normalizedIncomingValue = incomingValue.trim();

    if (!mergedValues.some((value) => value === normalizedIncomingValue)) {
        mergedValues.push(normalizedIncomingValue);
    }

    return mergedValues;
}

function getSearchOrderDirection(direction: string): "1" | "-1" | null {
    if (direction === "descending") {
        return "-1";
    }
    if (direction === "ascending") {
        return "1";
    }
    return null;
}

function isClearAllPropertiesName(name: string): boolean {
    return name.trim() === "*";
}

function parseSearchOrderTarget(name: string): { propertyType: string; propertyName: string } {
    const separatorIndex = name.indexOf(".");
    if (separatorIndex > 0 && separatorIndex < name.length - 1) {
        const prefix = name.substring(0, separatorIndex).trim();
        const propertyName = name.substring(separatorIndex + 1).trim();
        if ([urlQueryKeys.customProperties, urlQueryKeys.systemProperties, urlQueryKeys.validity, urlQueryKeys.displayProperties].includes(prefix)) {
            return { propertyType: prefix, propertyName };
        }
    }
    return { propertyType: urlQueryKeys.customProperties, propertyName: name.trim() };
}

export function BranchState({ children, unit, change, name = "", value, direction }: BranchStateProps): React.ReactElement {
    const createFilterBranch = useCreateFilterStateBranch();
    const custom = useCustomPropertiesFilter();
    const display = useDisplayPropertiesFilter();
    const searchOptions = useSearchOptionsFilter();
    const system = useSystemPropertiesFilter();

    // Get the value from the value child
    const valueChild = useMemo(() => findFirstComponentOfType(children, "arg-value"), [children]);
    const [argValue, setArgValue] = useState("");

    // Memoize the children with the value child removed.
    const branchChildren = useMemo(() => {
        const childrenArray = React.Children.toArray(children);
        return childrenArray.filter((child) => {
            if (!React.isValidElement(child)) return true;
            if (child.type.toString().toLowerCase() === "arg-value") {
                return false;
            }
            return true;
        });
    }, [children]);

    const filterState = useMemo(() => {
        let filterState: FilterState = {};
        switch (unit) {
            case "baseline":
                if (change === "clear") {
                    filterState.baseline = "";
                } else {
                    filterState.baseline = argValue || value;
                }
                break;
            case "configuration":
                if (change === "clear") {
                    filterState.configuration = "";
                } else {
                    filterState.configuration = argValue || value;
                }
                break;
            case "custom":
                if (change === "clear") {
                    if (isClearAllPropertiesName(name)) {
                        filterState.customProperties = {};
                    } else {
                        filterState.customProperties = { ...custom };
                        delete filterState.customProperties[name];
                    }
                } else {
                    filterState.customProperties = {
                        ...custom,
                        [name]: mergePropertyValues(custom[name], argValue || value),
                    };
                }
                break;
            case "display":
                if (change === "clear") {
                    if (isClearAllPropertiesName(name)) {
                        filterState.displayProperties = {};
                    } else {
                        filterState.displayProperties = { ...display };
                        delete filterState.displayProperties[name];
                    }
                } else {
                    filterState.displayProperties = {
                        ...display,
                        [name]: mergePropertyValues(display[name], argValue || value),
                    };
                }
                break;
            case "organization":
                if (change === "clear") {
                    filterState.organization = "";
                } else {
                    filterState.organization = argValue || value;
                }
                break;
            case "searchorder":
                if (change === "clear") {
                    if (isClearAllPropertiesName(name)) {
                        filterState.searchOptions = {
                            ...searchOptions,
                            order: [],
                        };
                    } else {
                        const { propertyType, propertyName } = parseSearchOrderTarget(name);
                        filterState.searchOptions = {
                            ...searchOptions,
                            order: (searchOptions.order || []).filter((orderDef) =>
                                !(orderDef.propertyType === propertyType && orderDef.propertyName === propertyName),
                            ),
                        };
                    }
                    break;
                }

                const shortDirection = getSearchOrderDirection(direction);
                if (!shortDirection) {
                    console.error(`[BranchState] Invalid search order direction "${direction}" for "${name}". Expected "ascending" or "descending".`);
                    break;
                }

                const { propertyType, propertyName } = parseSearchOrderTarget(name);
                if (!propertyName) {
                    console.error("[BranchState] Search order requires a non-empty property name.");
                    break;
                }

                const order: SearchOrder = { propertyName, propertyType, direction: shortDirection };
                const existingOrders = searchOptions.order || [];
                const filteredOrders = existingOrders.filter((orderDef) =>
                    !(orderDef.propertyType === propertyType && orderDef.propertyName === propertyName),
                );
                filterState.searchOptions = {
                    ...searchOptions,
                    order: [...filteredOrders, order],
                };
                break;
            case "system":
                if (change === "clear") {
                    if (isClearAllPropertiesName(name)) {
                        filterState.systemProperties = {};
                    } else {
                        filterState.systemProperties = { ...system };
                        delete filterState.systemProperties[name];
                    }
                } else {
                    filterState.systemProperties = {
                        ...system,
                        [name]: mergePropertyValues(system[name], argValue || value),
                    };
                }
                break;
            case "text":
                if (change === "clear") {
                    filterState.text = undefined;
                    filterState.localSearch = undefined;
                } else {
                    const textValue = argValue || value;
                    filterState.text = textValue;
                    filterState.localSearch = textValue;
                }
                break;
            case "validity":
                if (change === "clear") {
                    filterState.validity = "";
                } else {
                    filterState.validity = argValue || value;
                }
                break;
        }
        return filterState;
    }, [argValue, unit, change, name, value, direction, custom, display, searchOptions, system]);

    const BranchComponent = useMemo(() => {
        return createFilterBranch(filterState);
    }, [createFilterBranch, filterState]);

    return <>
        <BranchComponent>{branchChildren}</BranchComponent>
        <TextExtractor onExtractedText={setArgValue}>
            {valueChild?.props.children}
        </TextExtractor>
    </>
}
